import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';
import './VerifyEmail.css';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(60); // İlk başta 60 saniye

  useEffect(() => {
    // Register sayfasından gelen email'i al
    const state = location.state as { email?: string };
    if (state?.email) {
      setEmail(state.email);
    } else {
      // Email yoksa login'e yönlendir
      navigate('/login');
    }
  }, [location, navigate]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (index: number, value: string) => {
    // Sadece rakam kabul et
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Otomatik sonraki input'a geç
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace ile önceki input'a geç
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      // Son input'a focus et
      document.getElementById('code-5')?.focus();
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const verificationCode = code.join('');
    if (verificationCode.length !== 6) {
      setError('Lütfen 6 haneli kodu eksiksiz girin');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.verifyEmail(email, verificationCode);

      setSuccess('Email doğrulandı! Giriş yapılıyor...');

      // Token ve user bilgisini kaydet
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Ana sayfaya yönlendir
      setTimeout(() => {
        navigate('/');
        window.location.reload(); // Navbar'ı güncellemek için
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Doğrulama başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;

    setResendLoading(true);
    setError('');
    setSuccess('');

    try {
      await authAPI.resendVerificationCode(email);
      setSuccess('Yeni kod gönderildi! Emailinizi kontrol edin.');
      setCountdown(60); // 60 saniye bekle
      setCode(['', '', '', '', '', '']); // Kodu temizle
    } catch (error: any) {
      setError(error.response?.data?.message || 'Kod gönderilemedi');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="verify-email-container">
      <div className="verify-email-card">
        <div className="verify-header">
          <div className="verify-icon">
            <i className="bi bi-envelope-check"></i>
          </div>
          <h1>Email Doğrulama</h1>
          <p>
            <strong>{email}</strong> adresine gönderilen 6 haneli doğrulama kodunu girin
          </p>
        </div>

        <form onSubmit={handleVerify} className="verify-form">
          {error && (
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <i className="bi bi-check-circle-fill me-2"></i>
              {success}
            </div>
          )}

          <div className="code-inputs">
            {code.map((digit, index) => (
              <input
                key={index}
                id={`code-${index}`}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="code-input"
                autoComplete="off"
                disabled={loading}
              />
            ))}
          </div>

          <button
            type="submit"
            className="btn-verify"
            disabled={loading || code.join('').length !== 6}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Doğrulanıyor...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Doğrula
              </>
            )}
          </button>
        </form>

        <div className="verify-footer">
          <p className="resend-text">
            <button
              type="button"
              onClick={handleResendCode}
              className="btn-link"
              disabled={resendLoading || countdown > 0}
            >
              {resendLoading ? (
                'Gönderiliyor...'
              ) : (
                <>
                  Tekrar gönder
                  {countdown > 0 && <span className="countdown-timer"> ({countdown}s)</span>}
                </>
              )}
            </button>
          </p>

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="btn-back"
          >
            <i className="bi bi-arrow-left me-2"></i>
            Giriş sayfasına dön
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
